import {
  Component,
  OnDestroy,
  HostListener,
  AfterViewInit,
  inject,
  isDevMode,
  computed,
} from "@angular/core";
import { Subject } from "rxjs";

import { MessagingService } from "@bitwarden/common/abstractions/messaging.service";
import { MainSettingsRoute } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import { StoreLoaderService } from "@bitwarden/web-vault/app/services/dali/store.loader.service";
import { DataPluginStoreService } from "@bitwarden/web-vault/app/services/store/data-plugin/data-plugin.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { UpgradeNavigationService } from "@bitwarden/web-vault/app/services/subscription/upgrade-navigation.service";
import { Router } from "@angular/router";
import { basiqIntegrationEnabled } from "@bitwarden/web-vault/utils/flags";

@Component({
  selector: "app-settings",
  templateUrl: "settings.component.html",
})
export class SettingsComponent implements OnDestroy, AfterViewInit {
  private storeLoader = inject(StoreLoaderService);
  private dataPluginStore = inject(DataPluginStoreService);
  private messagingService: MessagingService = inject(MessagingService);
  private readonly router = inject(Router);
  private upgradeNavigation = inject(UpgradeNavigationService);
  private currentView: "mobile" | "web";
  private destroy$ = new Subject<void>();
  protected isDevMode = isDevMode();
  protected readonly MainSettingsRoute = MainSettingsRoute;
  protected userStoreService = inject(UserStoreService);
  protected storageUsage = this.userStoreService.user.usage;
  protected readonly basiqIntegrationEnabled = basiqIntegrationEnabled;

  constructor() {
    // The Billing link depends on the subscription history: if the startup fetch of it failed, ask
    // again when Settings opens rather than leave it unknown all session (#6034).
    void this.userStoreService.user.loadIfUnknown();
  }

  get activeSubscription() {
    return this.userStoreService.subscription.activeSubscription;
  }

  // NOTE: Daily Auto-Fetch is a UI placeholder only — the feature logic is NOT yet implemented.
  // Before shipping: wire this up through SettingsService/StateService so it is encrypted,
  // per-account, and synced.
  protected dailyAutoFetchEnabled = false;

  protected onDailyAutoFetchToggle(enabled: boolean) {
    this.dailyAutoFetchEnabled = enabled;
  }

  // Shown while the history is unknown (null: loading, or the fetch failed), so it isn't hidden
  // while loading for the users who have one; hidden once it's loaded empty for a non-premium user
  // (#6034). That's rare: gloss-api writes a default ironfly subscription on signup, though users
  // from before 2024-09-02 may have none.
  protected isSubscriptionPageEnabled = computed(() => {
    const subHistory = this.userStoreService.user.subscriptions();
    return subHistory === null || subHistory.length > 0 || !!this.activeSubscription()?.isPremium;
  });

  // Only prompt to upgrade once status is resolved: activeSubscription() is null both while
  // loading and for a free user, so without this the promo flashes for bundle-subscribed users
  // while their status is still resolving (#14137).
  protected showUpgradePromo = computed(
    () => this.userStoreService.subscription.isResolved() && !this.activeSubscription()?.isPremium,
  );

  @HostListener("window:resize")
  onResize() {
    this.checkScreenSize();
  }

  ngAfterViewInit() {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    const width = window.innerWidth;
    const mdValue = 767; // tailwind config

    if (width <= mdValue) {
      this.currentView = "mobile";
    } else if (width > mdValue) {
      this.currentView = "web";
    }
  }

  lock() {
    this.messagingService.send("lockVault");
  }

  logOut() {
    this.messagingService.send("logout");
  }

  exportVault() {
    if (isDevMode()) {
      /* eslint-disable no-console */
      console.info("*** User vault ***");
      console.dir(this.storeLoader.exportVaultData());
      console.info("*** Plugin data Forex / Country / Institutions ***");
      console.dir(this.dataPluginStore.forexRates.forexRates());
      console.dir(this.dataPluginStore.theCountryList.theCountryViews());
      console.dir(this.dataPluginStore.theInstitutionList.theInstitutionViews());
      this.router.navigate(["tools/export"]);
      /* eslint-enable no-console */
    } else {
      throw new Error("Exporting vault is not allowed in production mode");
    }
  }

  protected onUpgradeClick(): void {
    void this.upgradeNavigation.goToUpgrade();
  }

  protected getSettingsRoute(destination = "") {
    return [`/settings/${destination}`];
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
